<template>
  <div class="qty-component d-flex justify-content-around">
    <button class="rmv-qty-btn text-white" :disabled="isManualWeightLocked" @click="updateQuantity(-1)">
      <font-awesome-icon icon="fa-solid fa-minus"/>
    </button>
    <input class="qty-input" v-model="saleLine.Qty" @input="recalculateLine" type="number" :readonly="isReadOnly" :disabled="isManualWeightLocked" min="1"/>
    <button class="add-qty-btn text-white" :disabled="isManualWeightLocked" @click="updateQuantity(1)">
      <font-awesome-icon icon="fa-solid fa-plus"/>
    </button>
    <small v-if="isManualWeightLocked" class="qty-locked-note text-warning">
      Manual weight entry is disabled for this item. Use Read Scale instead.
    </small>
  </div>
</template>

<script>
// docs/FRONTEND.md — sale line Qty contract; qty-reset-on-save fix
import { computeQuantityChange } from '@/utils/saleLineQty';

export default {
  name: "CurrentSaleQty",
  props: {
    saleLine: {
      type: Object,
      required: true
    },
    saleNumber: {
      type: Number,
      required: true
    }
  },
  computed: {
    deviceSettings() {
      return this.$store.getters.getDeviceSettings;
    },
    isReadOnly() {
      if (!this.deviceSettings) return true;
      return !this.deviceSettings.SelectQty || (this.saleLine.instructions && this.saleLine.instructions.length > 0) || this.isManualWeightLocked;
    },
  },
  data() {
    return {
      isManualWeightLocked: false,
    };
  },
  async mounted() {
    await this.refreshManualWeightLock();
  },
  watch: {
    'saleLine.Plu': {
      handler() {
        this.refreshManualWeightLock();
      },
      immediate: true,
    },
  },
  methods: {
    /**
     * Resolve whether this PLU is a scale item without manual-weight permission.
     * Locks +/- and direct qty input when true.
     * @returns {Promise<void>}
     */
    async refreshManualWeightLock() {
      try {
        const canManualWeightEntry = await this.$store.dispatch('validateSecurityPermissionNoPrompt', 'manualWeightEntry');
        const set = await this.$store.dispatch('getStockSetting', this.saleLine?.Plu);
        this.isManualWeightLocked = Boolean(set?.scale || set?.Scale) && !canManualWeightEntry;
      } catch (err) {
        console.warn('[TablesSaleQty] Failed to resolve manual weight lock state', err);
        this.isManualWeightLocked = false;
      }
    },
    /**
     * Handles the +/- buttons for the table sale line.
     *
     * ALWAYS applies the qty change when newQty > 0 — cooking instructions
     * must NOT suppress the bump (qty-reset-on-save loss vector #1). When
     * instructions are present on +1, they are shown via a separate UX path
     * (`viewInstructions` emit) AFTER qty is updated.
     *
     * When reducing the last unit (newQty <= 0), the entire line is voided
     * via voidItem(). When reducing a multi-unit line with instructions,
     * an existing unit is also voided through the store for the audit trail.
     *
     * @param {number} change - +1 or -1
     * @returns {Promise<*>}
     */
    async updateQuantity(change) {
      if (this.isManualWeightLocked) {
        responses.showWarning('Manual quantity entry is disabled for scale items on your account. Use Read Scale instead.');
        return;
      }

      const decision = computeQuantityChange(this.saleLine, change);

      // Reducing below 1 means the whole line should be voided.
      if (decision.action === 'void') {
        return this.voidItem(this.saleLine);
      }

      // -1 on a multi-unit line with instructions: audit-void one unit via store
      if (change === -1) {
        let isAllowed = await this.$store.dispatch(`validateSecurityPermission`, `Void`);
        if (!isAllowed) return;

        const hasInstructions =
          Array.isArray(this.saleLine.instructions) && this.saleLine.instructions.length >= 1;
        if (hasInstructions) {
          if (this.$store.getters.getCurrentSale === 0 && this.$store.getters.getSaleLines.length === 0) {
            await this.$store.dispatch('getTableBySaleNumber', this.saleNumber);
          }
          const CurrentSale = this.$store.getters.getSaleLines;
          let item = CurrentSale.find(s => s.Plu === this.saleLine.Plu);
          this.$store.commit(`setOverrideDetails`, `Void Line (${this.saleLine.Description})`);
          this.$store.commit('voidItem', item);
        }
      }

      // Always set Qty — instructions must never block the bump
      this.saleLine.Qty = decision.qty;

      // Separate UX path: surface cooking instructions on +1 without skipping qty
      if (decision.viewInstructions) {
        this.$emit(`viewInstructions`, this.saleLine.Plu);
      }

      return this.recalculateLine();
    },

    /**
     * Resolve stable line identity for All Tables voids.
     * getAllTables list lines often only have Plu/Description/Qty/Unit/Line —
     * no id/saleline. BE logItemVoid fail-closes without id, so hydrate from
     * savetable detail before posting (ticket 22844 All Tables follow-up).
     * Does not open the table into the cart (avoids cart-void side effects).
     *
     * @param {Object} target - list or cart sale line
     * @param {number|string} saleNum - table sale number from props
     * @returns {Promise<{id: string|null, saleline: number|null}>}
     */
    async resolveVoidLineIdentity(target, saleNum) {
      let id = target.id || target.Id || target.lineId || target.currentSaleLineId || null;
      let saleline = target.saleline != null
        ? target.saleline
        : (target.SaleLine != null ? target.SaleLine : (target.saleLine != null ? target.saleLine : null));

      if (id && saleline != null) {
        return { id, saleline };
      }

      const sn = Number(saleNum);
      if (!Number.isFinite(sn) || sn <= 0) {
        return { id, saleline };
      }

      try {
        // Detail endpoint returns currentsale PKs; list endpoint does not.
        const detail = await this.$store.dispatch('getTableData', sn);
        const detailItems = detail?.items || detail?.Items || [];
        if (!Array.isArray(detailItems) || detailItems.length === 0) {
          return { id, saleline };
        }

        const targetPlu = String(target.Plu || target.plu || '');
        const targetDesc = String(target.Description || target.description || '');
        const match = detailItems.find((line) => {
          const lineId = line.id || line.Id || line.lineId || line.currentSaleLineId;
          if (id && lineId && String(lineId) === String(id)) return true;
          const pluMatch = String(line.Plu || line.plu || '') === targetPlu;
          if (!pluMatch) return false;
          // Prefer exact description when the same PLU appears twice on a table.
          if (targetDesc && String(line.Description || line.description || '') === targetDesc) {
            return true;
          }
          return pluMatch;
        }) || detailItems.find((line) => String(line.Plu || line.plu || '') === targetPlu);

        if (match) {
          id = id || match.id || match.Id || match.lineId || match.currentSaleLineId || null;
          if (saleline == null) {
            saleline = match.saleline != null
              ? match.saleline
              : (match.SaleLine != null ? match.SaleLine : (match.saleLine != null ? match.saleLine : null));
          }
          // Stamp onto the list object so a retry / parent remove uses the same PK.
          if (id && !target.id) {
            this.$set ? this.$set(target, 'id', id) : (target.id = id);
          }
          if (saleline != null && target.saleline == null) {
            this.$set ? this.$set(target, 'saleline', saleline) : (target.saleline = saleline);
          }
        }
      } catch (err) {
        console.warn('[TablesSaleQty] resolveVoidLineIdentity: detail hydrate failed', err);
      }

      return { id, saleline };
    },

    /**
     * Public method used to void the entire sale line from a table.
     * Mirrors the voidItem(item) API used by NewCurrentSaleLine /
     * CurrentSaleLine, but operates directly on tab.items -- the
     * table is NOT opened into currentSaleLines as a side-effect.
     *
     * Flow:
     *  1. Confirm with the user (and validate Void permission via
     *     validateSecurityPermission, same as updateQuantity).
     *  2. Commit setOverrideDetails for the audit trail.
     *  3. Hydrate id/saleline from savetable when list lines lack them
     *     (All Tables path — list payload has no currentsale PK).
     *  4. Dispatch logItemVoid with salenum from this.saleNumber (not
     *     getCurrentSale, which is empty until openTable).
      *  5. Emit 'void-item' for local remove:
      *     - salenum==0 (unsaved): always, silent — no error toast (#26827)
      *     - salenum!=0 (saved): only when logItemVoid succeeds (22844)
      *
      * Offline is allowed (TicketApp #26827 Option A): logItemVoid queues
      * via voidOutbox and returns success:true when enqueue works.
      * Whole-table void remains online-only (separate scope).
      * docs: FRONTEND.md cart line identity; services/voidOutbox.js
      *
      * @param {Object} item - The sale line to void (defaults to this.saleLine)
      * @returns {Promise<void>}
      */
    async voidItem(item) {
      const target = item || this.saleLine;
      if (!target) return;
      const description = target.Description || 'this item';

      const result = await Swal.fire({
        title: 'Void this item?',
        text: `You are about to void the last unit of "${description}". This step cannot be reversed!`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, Void it!',
        cancelButtonText: 'Cancel',
        showLoaderOnConfirm: true,
        preConfirm: async () => {
          let isAllowed = await this.$store.dispatch('validateSecurityPermission', 'Void');
          if (!isAllowed) {
            Swal.showValidationMessage('You do not have permission to void items.');
            return false;
          }
          return true;
        }
      });

      if (!result.isConfirmed) return;

      // Audit trail for the void.
      this.$store.commit('setOverrideDetails', `Void Line (${description})`);

      // All Tables sheet is not openTable'd — getCurrentSale is empty.
      // Carry salenum from the table card prop so BE archives the right sale.
      const saleNum = Number(this.saleNumber || target.salenum || target.SaleNum || 0) || 0;
      // Unsaved lines: local tab.items is source of truth after audit attempt.
      const isUnsavedLine = saleNum === 0;
      let voidSucceeded = false;

      try {
        const Storage = (await import('@/services/Storage')).Storage;
        const settings = await Storage.getDeviceSettings();
        const user = await Storage.getCurrentUser();
        const supervisor = await Storage.getSupervisor();

        // Hydrate currentsale PK when list lines only have Plu/Qty/Line.
        const identity = await this.resolveVoidLineIdentity(target, saleNum);

        // Same B5b / 22844 contract as cart voidItem/voidLine in sales-module.
        // docs: FRONTEND.md cart line identity; BE SupervisorOverrides.logItemVoid
        const voidObj = {
          VoidPlu: target.Plu,
          VoidDescritpiom: description,
          VoidQty: Number(target.Qty) || 0,
          VoidValue: Number(target.Line) || 0,
          SuperVisor: supervisor,
          User: user?.Username,
          PC: settings?.PC,
          // CRITICAL: table card sale number — not getCurrentSale (empty on All Tables).
          salenum: saleNum,
          SaleNum: saleNum,
          Reason: `Sale No. ${saleNum || 'unknown'}`,
          id: identity.id,
          saleline: identity.saleline,
        };

        // Saved lines need a server PK to archive; unsaved can still leave local UI.
        if (!voidObj.id && !isUnsavedLine) {
          await Swal.fire({
            icon: 'error',
            title: 'Void failed',
            text: 'Could not find this line on the server. Close the table sheet, reopen it, and try again.',
          });
          return;
        }

        // Always attempt audit API. Local emit gated by salenum (#26827 + 22844).
        const voidResult = voidObj.id || isUnsavedLine
          ? await this.$store.dispatch('logItemVoid', voidObj)
          : { success: false };
        voidSucceeded = !!(voidResult && voidResult.success);

        if (!voidSucceeded) {
          // salenum==0: silent — item leaves local table; BE audit is best-effort (#26827).
          if (isUnsavedLine) {
            // fall through to emit
          } else {
            // Saved lines: differentiate so cashiers do not see "Check the
            // connection" when the server rejected the void (#26827).
            // Offline enqueue failure/success already toasted inside logItemVoid.
            if (voidResult && voidResult.offline === true) {
              // offline fail already toasted
            } else if (voidResult && voidResult.businessReject) {
              // logItemVoid already showed the server message
            } else {
              await Swal.fire({
                icon: 'error',
                title: 'Void failed',
                text: 'This item was not removed. Check the connection, then try again.',
              });
            }
            // 22844: saved table lines stay until BE accepts/queues archive.
            return;
          }
        }
      } catch (err) {
        console.error('[TablesSaleQty] voidItem: logItemVoid failed', err);
        // Unsaved: silent local remove. Saved: show network error and keep line.
        if (!isUnsavedLine) {
          await Swal.fire({
            icon: 'error',
            title: 'Void failed',
            text: 'This item was not removed. Check the connection, then try again.',
          });
          return;
        }
      }

      // Parent removeItem splices tab.items. Emit on success always; also emit
      // for unsaved lines after a failed audit so local UI stays truthful.
      this.$emit('void-item', target);
    },

    // Recalculate the Line total whenever the quantity is updated
    async recalculateLine() {
      if(this.saleLine.Qty >= 0){
        // save item on current changes open table push to intructions
        this.saleLine.Line = this.saleLine.Unit * this.saleLine.Qty;
        this.recalculateTip()
        this.$emit('update-saleLine', this.saleLine);
      }else {
        return Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "value must be positive!",
        }).then(this.saleLine.Qty = 0);
      }
    },

    recalculateTip() {
      return this.$store.dispatch('calculateTipAmount');
    },

    askForPrice(item) {
      return Swal.fire({
        allowOutsideClick: false,
        showCancelButton: true,
        showLoaderOnConfirm: true,
        title: `Set Selling Price`,
        html: `<h6 >Description : ${item.Description}</h6>
               <h6 >Current Price : ${item.Unit.toFixed(2)}</h6>
               <input type="number" step="0.01" id="selling-prc" class="swal2-input" placeholder="Selling Price" style="max-width: 70%">`,
        confirmButtonText: 'Set Price',
        focusConfirm: false,
        preConfirm: async () => {
          let value = Swal.getPopup().querySelector('#selling-prc').value;
          if (!value) {
            Swal.showValidationMessage(`Please enter a valid Selling Price`);
          }
          return parseFloat(value);
        }
      }).then(result => {
        if (result.isConfirmed) {
          return result.value;
        }
        return parseFloat(item.SellingIncl.toFixed(2));
      });
    },
  }
}
</script>

<style scoped>
.qty-component {
  gap: 5px;
}

.qty-input {
  width: 50px;
  border: 1px solid #C9CACF;
  border-radius: 10px;
  text-align: center;
}

.add-qty-btn, .rmv-qty-btn {
  border-radius: 10px;
  width: 30px;
  height: 30px;
}

.qty-locked-note {
  display: block;
  width: 100%;
  text-align: center;
  line-height: 1.2;
}

button {
  background: #0092f7;
}
</style>
